import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ZapIcon, FolderIcon, BizFirstIcon, PencilIcon, TrashIcon } from '@bizfirst/common-react';
import type { ProjectSummary } from './types';

const PAGE_SIZE_KEY = 'project-management-react-page-size';
const PAGE_SIZE_OPTIONS = [6, 12, 18, 24];

const readStoredPageSize = (): number => {
  try {
    const stored = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZE_OPTIONS.includes(stored) ? stored : 24;
  } catch {
    // localStorage unavailable (private mode, blocked site data) — fall back silently.
    return 24;
  }
};

// Both hosts unmount this component when a Project is opened (App Studio swaps to its canvas,
// Flow Studio to its workflow editor) and remount it on "back", so the page must survive a remount.
// It is kept in sessionStorage, keyed by the current path so each list in an app is independent.
// A module-level variable was used before; it survived in-app remounts but was lost on any full
// page load, which is why "back" could still land on page 1.
const pageStorageKey = () => `project-management-react-page:${window.location.pathname}`;

const readStoredPage = (): number => {
  try {
    const stored = Number(sessionStorage.getItem(pageStorageKey()));
    return Number.isInteger(stored) && stored > 0 ? stored : 1;
  } catch {
    // sessionStorage unavailable (private mode, blocked site data) — start at page 1.
    return 1;
  }
};

const writeStoredPage = (page: number) => {
  try {
    sessionStorage.setItem(pageStorageKey(), String(page));
  } catch {
    // Ignore write failures; paging still works for this mount.
  }
};

// Generic over T (the host's real Project entity, a superset of ProjectSummary) rather than
// fixed to ProjectSummary, so callbacks hand the host back its own concrete type (Flow Studio's
// full `Project` — TenantID, audit columns, ResID, etc. — not a narrowed-down summary it would
// then have to re-widen/cast to pass into its own handlers).
export interface ProjectListViewProps<T extends ProjectSummary = ProjectSummary> {
  projects: T[];
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  onCreateClick: () => void;
  onEditClick: (project: T) => void;
  onDeleteClick: (project: T) => void;
  onDesignClick: (project: T) => void;
  /** Dark/light hex palette to use — matches flow-studio-designer's UnifiedDashboard exactly so
   *  that host renders pixel-identically after adopting this component. App Studio's host can
   *  instead rely on its own CSS custom properties by passing theme="dark" (the values below are
   *  only literal fallbacks; every color is emitted as `var(--token, fallback)` so a host that
   *  defines the token wins). */
  theme?: 'dark' | 'light';
  /** App Studio wants a real text-search box above the grid; Flow Studio's existing dashboard
   *  never had one and must not regress — defaults to false (no search box rendered) so an
   *  existing consumer that omits this prop is unaffected. */
  searchable?: boolean;
  searchPlaceholder?: string;
  title?: string;
  subtitle?: string;
  createLabel?: string;
  designLabel?: string;
  emptyMessage?: string;
  /** Label for the secondary action shown top-right of each card (e.g. Flow Studio's
   *  "Processes" drill-down button). Omit to not render a secondary action at all. */
  secondaryAction?: { label: string; onClick: (project: T) => void };
}

export function ProjectListView<T extends ProjectSummary = ProjectSummary>({
  projects,
  loading,
  error,
  onRetry,
  onCreateClick,
  onEditClick,
  onDeleteClick,
  onDesignClick,
  theme = 'dark',
  searchable = false,
  searchPlaceholder = 'Search projects…',
  title = 'Projects',
  subtitle,
  createLabel = '+ Create Project',
  designLabel = 'Design',
  emptyMessage = 'No projects found. Create one to get started.',
  secondaryAction,
}: ProjectListViewProps<T>) {
  const [search, setSearch] = useState('');
  const [page, setPageState] = useState<number>(readStoredPage);
  const [pageSize, setPageSize] = useState<number>(readStoredPageSize);

  const setPage = (p: number) => {
    writeStoredPage(p);
    setPageState(p);
  };

  const filtered = useMemo(() => {
    if (!searchable) return projects;
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      p => p.Name.toLowerCase().includes(q) || (p.Description ?? '').toLowerCase().includes(q),
    );
  }, [projects, search, searchable]);

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize],
  );

  const handlePageChange = (p: number) => setPage(Math.max(1, Math.min(p, totalPages)));
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(size));
    } catch {
      // best-effort only
    }
  };

  const dark = theme === 'dark';
  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    background: `var(--color-bg-primary, ${dark ? '#020617' : '#f9fafb'})`,
    padding: '24px 40px 40px',
    overflowY: 'auto',
    boxSizing: 'border-box',
  };
  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  };
  const titleStyle: React.CSSProperties = {
    fontSize: '24px',
    fontWeight: 700,
    color: `var(--color-text-primary, ${dark ? '#f8fafc' : '#111827'})`,
  };
  const createButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 20px',
    background: dark ? 'rgba(34, 197, 94, 0.15)' : '#16a34a',
    color: '#ffffff',
    border: dark ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid #16a34a',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gridAutoRows: 'auto',
    gap: '24px',
  };
  const emptyColor = `var(--color-text-tertiary, ${dark ? '#94a3b8' : '#64748b'})`;

  const searchRowStyle: React.CSSProperties = { position: 'relative', maxWidth: 360, marginBottom: 20 };
  const searchInputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    background: `var(--color-bg-secondary, ${dark ? '#16161e' : '#ffffff'})`,
    border: `1px solid var(--color-border, ${dark ? '#333' : '#d1d5db'})`,
    borderRadius: 'var(--border-radius-md, 6px)',
    color: `var(--color-text-primary, ${dark ? '#e8e8f0' : '#111827'})`,
    padding: '9px 12px 9px 34px',
    fontSize: '13px',
    outline: 'none',
  };

  const shouldShowPagination = totalCount > pageSize;

  return (
    <div className="pmr-project-list" style={containerStyle}>
      <div className="pmr-list-header" style={headerStyle}>
        <div>
          <h1 style={titleStyle}>{title}</h1>
          {subtitle && <p className="pmr-list-subtitle">{subtitle}</p>}
        </div>
        <button className="pmr-create-button" style={createButtonStyle} onClick={onCreateClick}>
          {createLabel}
        </button>
      </div>

      {searchable && (
        <div className="pmr-search" style={searchRowStyle}>
          <input
            aria-label="Search projects"
            style={searchInputStyle}
            placeholder={searchPlaceholder}
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: emptyColor }}>Loading...</div>
      ) : error ? (
        <div
          style={{
            textAlign: 'center',
            padding: '60px',
            backgroundColor: dark ? '#3f1f1f' : '#fee',
            border: `2px solid ${dark ? '#7f1f1f' : '#fcc'}`,
            borderRadius: '8px',
            color: dark ? '#fca5a5' : '#c33',
          }}
        >
          <h2 style={{ marginTop: 0, marginBottom: '12px' }}>Connection Error</h2>
          <p style={{ margin: '8px 0' }}>{error}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              style={{
                marginTop: '16px',
                padding: '10px 20px',
                background: dark ? '#7f1f1f' : '#fcc',
                color: dark ? '#fca5a5' : '#c33',
                border: `1px solid ${dark ? '#fca5a5' : '#c33'}`,
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              <ZapIcon size={14} /> Retry
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="pmr-grid" style={gridStyle}>
            {paged.map(project => (
              <ProjectCard
                key={project.ProjectID}
                project={project}
                designLabel={designLabel}
                onDesignClick={() => onDesignClick(project)}
                onEditClick={() => onEditClick(project)}
                onDeleteClick={() => onDeleteClick(project)}
                secondaryAction={
                  secondaryAction
                    ? { label: secondaryAction.label, onClick: () => secondaryAction.onClick(project) }
                    : undefined
                }
              />
            ))}
          </div>

          {totalCount === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: emptyColor }}>{emptyMessage}</div>
          )}

          {shouldShowPagination && (
            <PaginationBar
              page={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              itemLabel="projects"
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </>
      )}
    </div>
  );
}

// ── Project card ────────────────────────────────────────────────────────────

interface ProjectCardProps {
  project: ProjectSummary;
  designLabel: string;
  onDesignClick: () => void;
  onEditClick: () => void;
  onDeleteClick: () => void;
  secondaryAction?: { label: string; onClick: () => void };
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  designLabel,
  onDesignClick,
  onEditClick,
  onDeleteClick,
  secondaryAction,
}) => {
  const [hovered, setHovered] = useState(false);
  const cardColor = project.Color || '#309B48';

  return (
    <div
      className="pmr-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '18px',
        borderRadius: '10px',
        border: `1px solid ${hovered ? cardColor : cardColor + '66'}`,
        background: 'var(--color-bg-secondary, #16161e)',
        boxShadow: hovered ? `0 4px 20px ${cardColor}33` : 'none',
        cursor: 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onDesignClick}
    >
      <div className="pmr-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {project.IconUrl ? (
            <BizFirstIcon icon={project.IconUrl} size={24} iconStyle={project.Color ? { color: project.Color } : undefined} />
          ) : (
            <FolderIcon size={24} color={project.Color || undefined} />
          )}
        </span>
        {secondaryAction && (
          <button
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--color-text-tertiary, #94a3b8)',
              background: 'transparent',
              border: '1px solid var(--color-border, #333)',
              borderRadius: 6,
              padding: '3px 8px',
              cursor: 'pointer',
            }}
            onClick={e => {
              e.stopPropagation();
              secondaryAction.onClick();
            }}
          >
            {secondaryAction.label}
          </button>
        )}
      </div>

      <div
        className="pmr-card-title"
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: 'var(--color-text-primary, var(--text-primary, #f8fafc))',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {project.Name}
      </div>

      {project.Description && (
        <p
          style={{
            margin: '6px 0 0',
            fontSize: 13,
            color: 'var(--color-text-tertiary, #94a3b8)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {project.Description}
        </p>
      )}

      <div style={{ flex: 1, minHeight: 12 }} />

      <div
        className="pmr-card-actions"
        style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 10, borderTop: '1px solid var(--color-border, #2a2a3a)' }}
        onClick={e => e.stopPropagation()}
      >
        <button
          style={actionBtnStyle}
          onClick={e => {
            e.stopPropagation();
            onDesignClick();
          }}
        >
          <ZapIcon size={12} /> {designLabel}
        </button>
        <span style={{ color: 'var(--color-border, #333)' }}>|</span>
        <button
          style={actionBtnStyle}
          onClick={e => {
            e.stopPropagation();
            onEditClick();
          }}
        >
          <PencilIcon size={12} /> Edit
        </button>
        <span style={{ color: 'var(--color-border, #333)' }}>|</span>
        <button
          style={{ ...actionBtnStyle, color: '#f87171' }}
          onClick={e => {
            e.stopPropagation();
            onDeleteClick();
          }}
        >
          <TrashIcon size={12} /> Delete
        </button>
      </div>
    </div>
  );
};

const actionBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: 'transparent',
  border: 'none',
  color: 'var(--color-text-secondary, #cbd5e1)',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  padding: 0,
};

// ── Pagination ───────────────────────────────────────────────────────────────

export interface PaginationBarProps {
  page: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  itemLabel: string;
  onPageChange: (page: number) => void;
  /** Required when the "Per page" dropdown is shown. Omit together with `showPageSize={false}`
   *  for a host with a fixed page size. */
  onPageSizeChange?: (size: number) => void;
  /** Show the "Per page" dropdown (options 6/12/18/24). Defaults to true; set false for a fixed page size. */
  showPageSize?: boolean;
  /** Options for the "Per page" dropdown. Defaults to 6/12/18/24. */
  pageSizeOptions?: number[];
}

export const PaginationBar: React.FC<PaginationBarProps> = ({
  page,
  totalPages,
  totalCount,
  pageSize,
  itemLabel,
  onPageChange,
  onPageSizeChange,
  showPageSize = true,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}) => {
  const from = totalCount === 0 ? 0 : Math.min((page - 1) * pageSize + 1, totalCount);
  const to = Math.min(page * pageSize, totalCount);

  // Lists that unmount when a record opens (and remount on "back") lose their page. The bar keeps the
  // current page per route in sessionStorage and restores it once on mount, for any host that uses it.
  // The key includes the list's label, so two lists on the same route (for example the tabs of one page)
  // keep separate pages. Restore waits until the data exists, because the bar can mount before it arrives.
  const pagingKey = `pmr-paging:${window.location.pathname}:${itemLabel}`;
  const hasMounted = useRef(false);
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || totalCount === 0) return;
    restored.current = true;
    let stored: { page?: number; pageSize?: number } | null = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(pagingKey) ?? 'null');
    } catch {
      stored = null;
    }
    const storedPage = stored?.page;
    if (stored?.pageSize === pageSize && Number.isInteger(storedPage) && storedPage! > 1 && storedPage! <= totalPages && storedPage !== page) {
      onPageChange(storedPage!);
    }
  }, [totalCount, totalPages, pageSize, page, pagingKey]);
  useEffect(() => {
    // The first run is the mount itself, before any restore; skip it so the initial page isn't saved over the stored one.
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    try {
      sessionStorage.setItem(pagingKey, JSON.stringify({ page, pageSize }));
    } catch {
      // Storage unavailable: paging still works for this mount.
    }
  }, [pagingKey, page, pageSize]);

  // Show the bar only when there is more than one small page of data. Hooks above still run every render.
  if (totalCount <= 6) return null;

  const btnStyle = (disabled: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 34,
    borderRadius: 8,
    border: '1px solid var(--border-default, rgba(34,197,94,0.25))',
    background: disabled ? 'var(--bg-main, rgba(51,65,85,0.25))' : 'var(--bg-raised, rgba(34,197,94,0.10))',
    color: disabled ? 'var(--text-muted, #475569)' : 'var(--color-primary, #4ade80)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 18,
    lineHeight: 1,
    flexShrink: 0,
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 40,
        padding: '10px 16px',
        borderRadius: 10,
        background: 'var(--bg-raised, rgba(15,23,42,0.7))',
        border: '1px solid var(--border-default, rgba(34,197,94,0.15))',
        userSelect: 'none',
      }}
    >
      {showPageSize && onPageSizeChange && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted, #94a3b8)' }}>Per page:</span>
          <select
            value={pageSize}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            style={{
              padding: '5px 10px',
              borderRadius: 8,
              border: '1px solid var(--border-default, rgba(34,197,94,0.25))',
              background: 'var(--bg-main, #0f172a)',
              color: 'var(--text-primary, #e2e8f0)',
              fontSize: 13,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            {pageSizeOptions.map(opt => (
              <option key={opt} value={opt} style={{ background: 'var(--bg-main, #0f172a)', color: 'var(--text-primary, #e2e8f0)' }}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button style={btnStyle(page <= 1)} disabled={page <= 1} onClick={() => onPageChange(page - 1)} aria-label="Previous page">
          ‹
        </button>
        <span style={{ fontSize: 13, color: 'var(--text-muted, #94a3b8)', minWidth: 180, textAlign: 'center' }}>
          {totalCount > 0 ? `Showing ${from}–${to} of ${totalCount} ${itemLabel}` : `Page ${page} of ${totalPages}`}
        </span>
        <button
          style={btnStyle(page >= totalPages)}
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
    </div>
  );
};
